import React, { useEffect, useState } from 'react'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { Card } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material'
import { toast } from 'react-toastify'

const DEFAULT_MODELS = [
  { value: 'openai:gpt-4o-mini', label: 'GPT-4o mini (OpenAI)' },
  { value: 'openai:gpt-4.1', label: 'GPT-4.1 (OpenAI)' },
  { value: 'anthropic:claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
  { value: 'google:gemini-1.5-pro', label: 'Gemini 1.5 Pro' }
]

function getModelLabel(value) {
  if (!value) return 'AI_DEFAULT_MODEL (configurazione backend)'
  const found = DEFAULT_MODELS.find((m) => m.value === value)
  if (found) return found.label
  return value
}

function AiConversationsAdmin() {
  const { user } = useAuth()
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchId, setSearchId] = useState('')
  const [selectedModel, setSelectedModel] = useState('')
  const [updatingId, setUpdatingId] = useState(null)

  const roleName = (user?.role_name || user?.role || '').toLowerCase()
  const isAdmin = roleName === 'admin'

  useEffect(() => {
    if (!isAdmin) return
    loadConversations()
  }, [isAdmin])

  const loadConversations = async () => {
    try {
      setLoading(true)
      const res = await api.get('/ai/conversations?limit=50&offset=0')
      setConversations(res.data.data || [])
    } catch (e) {
      toast.error('Errore nel caricamento delle conversazioni AI')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchId) {
      loadConversations()
      return
    }
    try {
      setLoading(true)
      const id = Number(searchId)
      const res = await api.get(`/ai/conversations/${id}?limit=1`)
      if (res.data && res.data.conversation) {
        setConversations([res.data.conversation])
      } else {
        setConversations([])
        toast.info('Nessuna conversazione trovata')
      }
    } catch (e) {
      setConversations([])
      toast.error('Conversazione non trovata')
    } finally {
      setLoading(false)
    }
  }

  const handleChangeModel = async (conversationId) => {
    if (!selectedModel) {
      toast.error('Seleziona un modello')
      return
    }
    try {
      setUpdatingId(conversationId)
      await api.patch(`/ai/conversations/${conversationId}/model`, {
        model: selectedModel
      })
      toast.success('Modello aggiornato')
      await loadConversations()
    } catch (e) {
      const message = e?.response?.data?.message || 'Errore durante l aggiornamento del modello'
      toast.error(message)
    } finally {
      setUpdatingId(null)
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold mb-2">AI – Modelli conversazioni</h1>
        <p className="text-sm text-muted-foreground">Accesso riservato agli amministratori.</p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">AI – Modelli conversazioni</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Da qui puoi forzare il modello AI per singole conversazioni. Se non imposti nulla, viene usato il
            modello di default configurato nella sezione <span className="font-medium">AI Settings</span>.
          </p>
        </div>
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Filtra per ID conversazione</span>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                className="w-32"
                placeholder="Es. 16"
              />
              <Button variant="outline" size="sm" onClick={handleSearch} disabled={loading}>
                Cerca
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchId('')
                  loadConversations()
                }}
                disabled={loading}
              >
                Reset
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Modello da impostare</span>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="ai-model-select-label">Modello AI</InputLabel>
              <Select
                labelId="ai-model-select-label"
                value={selectedModel}
                label="Modello AI"
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {DEFAULT_MODELS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Seleziona un modello dall’elenco e poi usa il pulsante <span className="font-medium">Imposta modello</span> sulla riga desiderata. Le conversazioni senza modello esplicito continueranno a usare il modello di default.
        </p>
      </Card>

      <Card className="p-4">
        {loading && <div className="text-sm text-muted-foreground">Caricamento conversazioni...</div>}
        {!loading && conversations.length === 0 && (
          <div className="text-sm text-muted-foreground">Nessuna conversazione AI trovata.</div>
        )}
        {!loading && conversations.length > 0 && (
          <div className="space-y-2">
            {conversations.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b last:border-b-0 py-2"
              >
                <div className="space-y-1">
                  <div className="text-sm font-medium">
                    Conversazione #{c.id} {c.title ? `– ${c.title}` : ''}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Utente {c.user_id} • Tenant {c.tenant_id || 'n/d'} • Stato {c.status}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Modello corrente: {c.model || 'default (AI_DEFAULT_MODEL)'}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-end gap-1 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Modello effettivo:</span>
                      <span className="font-medium">{getModelLabel(c.model)}</span>
                    </div>
                    <div className="flex items-center justify-end">
                      {c.model ? (
                        <Badge variant="outline" className="text-xs">
                          Custom
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={updatingId === c.id || !selectedModel}
                    onClick={() => handleChangeModel(c.id)}
                  >
                    {updatingId === c.id ? 'Salvataggio...' : 'Imposta modello'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

export default AiConversationsAdmin
